/**
 * Converts API GraphSnapshot data into a Graphology graph instance
 * for use with Sigma.js rendering.
 */

import Graph from 'graphology';
import type { GraphSnapshot } from '@/services/api';
import {
  ENTITY_CLASS_COLORS,
  ENTITY_CLASS_LABELS,
} from '@/services/api';

export interface NodeAttributes {
  label: string;
  x: number;
  y: number;
  size: number;
  color: string;
  entityClass: string;
  memberCount: number;
  confidence: number;
  completeness: number;
  resolutionType: string | null;
  borderColor?: string;
  type?: string;
  highlighted?: boolean;
  hidden?: boolean;
}

export interface EdgeAttributes {
  size: number;
  color: string;
  edgeType: string;
  linkSource: string;
  similarity: number | null;
  isCrossClass: boolean;
  type?: string;
  hidden?: boolean;
}

const EDGE_TYPE_COLORS: Record<string, string> = {
  near_duplicate: '#ef4444',
  strongly_related: '#f59e0b',
  related: '#2dd4bf',
  loosely_related: '#64748b',
  weak: '#334155',
  structural: '#818cf8',
};

function getEntityColor(ec: string): string {
  return ENTITY_CLASS_COLORS[ec] ?? '#94a3b8';
}

function getEdgeColor(linkSource: string, edgeType: string): string {
  if (linkSource === 'semantic') {
    return EDGE_TYPE_COLORS[edgeType] ?? 'rgba(51,65,85,0.3)';
  }
  return '#818cf8';
}

function getEdgeSize(linkSource: string, edgeType: string, similarity: number | null): number {
  if (linkSource === 'structural') return 1.5;
  if (edgeType === 'near_duplicate') return 2;
  if (edgeType === 'strongly_related') return 1.5;
  if (similarity != null) return Math.max(0.5, similarity * 2);
  return 0.5;
}

export function buildGraphologyGraph(snapshot: GraphSnapshot): Graph<NodeAttributes, EdgeAttributes> {
  const graph = new Graph<NodeAttributes, EdgeAttributes>({ multi: false, type: 'undirected' });

  const nodeSet = new Set<string>();

  for (const node of snapshot.nodes) {
    if (nodeSet.has(node.id)) continue;
    nodeSet.add(node.id);

    const hasPosition = node.x != null && node.y != null;

    graph.addNode(node.id, {
      label: node.name,
      x: hasPosition ? node.x! : (Math.random() - 0.5) * 100,
      y: hasPosition ? node.y! : (Math.random() - 0.5) * 100,
      size: Math.max(4, Math.min(20, Math.log2(node.member_count + 1) * 4)),
      color: getEntityColor(node.entity_class),
      entityClass: node.entity_class,
      memberCount: node.member_count,
      confidence: node.confidence,
      completeness: node.completeness,
      resolutionType: node.resolution_type,
    });
  }

  for (const link of snapshot.links) {
    if (!graph.hasNode(link.source) || !graph.hasNode(link.target)) continue;
    if (link.source === link.target) continue;

    const edgeKey = `${link.source}--${link.target}`;
    if (graph.hasEdge(edgeKey)) continue;

    try {
      graph.addEdgeWithKey(edgeKey, link.source, link.target, {
        size: getEdgeSize(link.link_source, link.edge_type, link.similarity),
        color: getEdgeColor(link.link_source, link.edge_type),
        edgeType: link.edge_type,
        linkSource: link.link_source,
        similarity: link.similarity,
        isCrossClass: link.is_cross_class,
      });
    } catch {
      // skip duplicate edges from undirected graph
    }
  }

  return graph;
}

export function mergeNeighborhoodIntoGraph(
  graph: Graph<NodeAttributes, EdgeAttributes>,
  snapshot: GraphSnapshot,
): void {
  for (const node of snapshot.nodes) {
    if (graph.hasNode(node.id)) continue;

    const hasPosition = node.x != null && node.y != null;

    graph.addNode(node.id, {
      label: node.name,
      x: hasPosition ? node.x! : (Math.random() - 0.5) * 100,
      y: hasPosition ? node.y! : (Math.random() - 0.5) * 100,
      size: Math.max(4, Math.min(20, Math.log2(node.member_count + 1) * 4)),
      color: getEntityColor(node.entity_class),
      entityClass: node.entity_class,
      memberCount: node.member_count,
      confidence: node.confidence,
      completeness: node.completeness,
      resolutionType: node.resolution_type,
    });
  }

  for (const link of snapshot.links) {
    if (!graph.hasNode(link.source) || !graph.hasNode(link.target)) continue;
    if (link.source === link.target) continue;
    const edgeKey = `${link.source}--${link.target}`;
    if (graph.hasEdge(edgeKey)) continue;

    try {
      graph.addEdgeWithKey(edgeKey, link.source, link.target, {
        size: getEdgeSize(link.link_source, link.edge_type, link.similarity),
        color: getEdgeColor(link.link_source, link.edge_type),
        edgeType: link.edge_type,
        linkSource: link.link_source,
        similarity: link.similarity,
        isCrossClass: link.is_cross_class,
      });
    } catch {
      // skip duplicates
    }
  }
}

export { ENTITY_CLASS_COLORS, ENTITY_CLASS_LABELS, EDGE_TYPE_COLORS };
